<template>
    <div class="content-block dx-card">
        <h2>User Metric Reporting</h2>
        <p class="session-note">
            <i class="dx-icon-info"></i>
            A session ends after 120 minutes of inactivity. Any activity after that gap starts a new session.
        </p>
    </div>

    <div class="content-block dx-card" id="dataDiv">
        <DxDataGrid class="dx-card wide-card" :data-source="userMetrics" key-expr="userId" :hover-state-enabled="true"
            :selection="{ mode: 'single' }" :show-borders="true" :columns-auto-width="true" :column-min-width="50"
            :allow-column-resizing="true" :column-resizing-mode="columnResize" :allow-column-reordering="true"
            :style="{ 'max-height': datagridHeight }"
            no-data-text="No data." @initialized="saveGridInstance" @exporting="onExporting">
            <DxLoadPanel :enabled="true" />
            <DxColumnChooser :enabled="true" mode="select" />
            <DxFilterRow :visible="true" />
            <DxHeaderFilter :visible="true" />
            <DxScrolling mode="standard" />
            <DxExport :enabled="true" />
            <DxColumnFixing :enabled="true" />
            <DxPager :show-info="true" :show-page-size-selector="true" :allowed-page-sizes="[20, 50, 100]"
                :show-navigation-buttons="true" />

            <DxToolbar>
                <DxItem location="before" template="dayRangeTemplate" />
                <DxItem location="after" name="columnChooserButton" />
                <DxItem location="after" template="refreshRecordTemplate" />
                <DxItem location="after" template="removeFilterRecordTemplate" />
                <DxItem location="after" name="exportButton" />
            </DxToolbar>

            <template #dayRangeTemplate>
                <DxSelectBox :items="dayRangeOptions" display-expr="text" value-expr="value"
                    v-model:value="selectedDays" label="Date Range" label-mode="floating" :width="180"
                    @value-changed="onDayRangeChanged" />
            </template>
            <template #refreshRecordTemplate>
                <DxButton icon="refresh" @click="refreshData" />
            </template>
            <template #removeFilterRecordTemplate>
                <DxButton icon="filter" @click="removeFilter" />
            </template>

            <DxColumn data-field="userName" caption="User Name" />
            <DxColumn data-field="lastLogin" caption="Last Login" data-type="datetime"
                format="MMM d, yyyy h:mm a" />
            <DxColumn data-field="lastActivity" caption="Last Activity" data-type="datetime"
                format="MMM d, yyyy h:mm a" />
            <DxColumn data-field="loginsInPeriod" :caption="`Logins Last ${selectedDays} Days`" data-type="number" />
            <DxColumn data-field="avgApiCallsPerDay" :caption="`Avg API Calls/Day (Last ${selectedDays} Days)`" data-type="number"
                :format="{ type: 'fixedPoint', precision: 2 }" />
            <DxColumn data-field="numberOfSessionsToday" caption="Sessions Today" data-type="number" />
            <DxColumn data-field="lastSessionLengthSeconds" caption="Last Session Length" data-type="number"
                :customize-text="formatDuration" />
            <DxColumn data-field="averageNumberOfSessionsPerDay" caption="Avg Sessions/Day" data-type="number"
                :format="{ type: 'fixedPoint', precision: 2 }" />
            <DxColumn data-field="sessionsInPeriod" :caption="`Sessions Last ${selectedDays} Days`" data-type="number" />
            <DxColumn data-field="averageSessionLengthSeconds" :caption="`Avg Session Length (Last ${selectedDays} Days)`" data-type="number"
                :customize-text="formatDuration" />
        </DxDataGrid>
    </div>

    <DxLoadPanel v-model:visible="loadingVisible" message="Loading..." :show-indicator="true" :show-pane="true"
        :shading="true" :hide-on-outside-click="false" :on-shown="onLoadPanelShown" :on-hidden="onLoadPanelHidden"
        shading-color="rgba(0,0,0,0.4)" />
</template>

<script>
import {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxFilterRow,
    DxScrolling,
    DxColumnChooser,
    DxColumnFixing,
    DxPager,
    DxItem,
    DxToolbar,
    DxExport,
} from "devextreme-vue/data-grid";
import { DxButton } from "devextreme-vue/button";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxLoadPanel } from "devextreme-vue/load-panel";

export default {
    components: {
        DxDataGrid,
        DxColumn,
        DxHeaderFilter,
        DxFilterRow,
        DxScrolling,
        DxColumnChooser,
        DxLoadPanel,
        DxButton,
        DxSelectBox,
        DxToolbar,
        DxItem,
        DxExport,
        DxColumnFixing,
        DxPager,
    },
    created() {
        this.loadPageData();
    },
    mounted() {
        window.addEventListener("resize", this.setDimensions);
        this.setDimensions();
    },
    unmounted() {
        window.removeEventListener("resize", this.setDimensions);
    },
    data() {
        return {
            columnResize: "widget",
            dataGridInstance: null,
            loadingVisible: false,
            isLoadPanelVisible: false,
            datagridHeight: "40vh",
            currentArea: null,
            userMetrics: [],
            error: null,
            latestRequestId: 0,
            selectedDays: 30,
            dayRangeOptions: [
                { value: 7, text: "Last 7 Days" },
                { value: 30, text: "Last 30 Days" },
                { value: 90, text: "Last 90 Days" },
                { value: 180, text: "Last 180 Days" },
                { value: 365, text: "Last 365 Days" },
            ],
        };
    },
    computed: {},
    methods: {
        onLoadPanelShown() {
            setTimeout(() => {
                this.loadingVisible = false;
            }, 10000);
        },
        onLoadPanelHidden() { },
        saveGridInstance: function (e) {
            this.dataGridInstance = e.component;
        },
        setDimensions() {
            const h = document.documentElement.clientHeight;
            this.datagridHeight = h - 250 + "px";
        },
        removeFilter() {
            this.dataGridInstance.clearFilter();
            this.dataGridInstance.clearSorting();
        },
        refreshData() {
            this.loadPageData();
        },
        onDayRangeChanged() {
            this.loadPageData();
        },
        async loadPageData(refresh = false) {
            // Ignore responses from older requests if the date range changed while they were loading.
            const requestId = ++this.latestRequestId;
            this.loadingVisible = true;
            try {
                await this.$store.dispatch("userMetrics/loadUserMetrics", {
                    forceRefresh: refresh,
                    days: this.selectedDays,
                });
                if (requestId !== this.latestRequestId) return;
                this.userMetrics = this.$store.getters["userMetrics/getUserMetrics"];
            } catch (error) {
                if (requestId !== this.latestRequestId) return;
                this.error = error.message || "Unexpected Error.";
            }
            this.loadingVisible = false;
        },
        // Formats a duration in seconds as e.g. "1d 6h 59m", "2h 05m", "18m 12s" or "45s".
        formatDuration(cellInfo) {
            const totalSeconds = Math.round(cellInfo.value);
            if (cellInfo.value == null || isNaN(totalSeconds)) return "";

            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const pad = (n) => String(n).padStart(2, "0");

            if (days > 0) return `${days}d ${hours}h ${pad(minutes)}m`;
            if (hours > 0) return `${hours}h ${pad(minutes)}m`;
            if (minutes > 0) return `${minutes}m ${pad(seconds)}s`;
            return `${seconds}s`;
        },
        formatDate(date) {
            if (!date) return "";
            const formattedDate = new Date(date);

            return formattedDate instanceof Date && !isNaN(formattedDate)
                ? formattedDate.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                })
                : "";
        },
        onExporting(e) {
            e.fileName = `UserMetricsDataExport_${new Date().toISOString().slice(0, 10)}`;
        },
    },
};
</script>

<style scoped>
h2 {
    margin-left: 1em;
}

.session-note {
    margin: 0 0 1em 1.5em;
    font-size: 0.9em;
    opacity: 0.75;
    display: flex;
    align-items: center;
    gap: 0.4em;
}
</style>
